import React from 'react';
import { Youtube, Linkedin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
interface FooterProps {
  logo?: {
    url: string;
    src: string;
    alt: string;
    title: string;
  };
  sections?: Array<{
    title: string;
    links: Array<{
      name: string;
      href: string;
    }>;
  }>;
  description?: string;
  socialLinks?: Array<{
    icon: React.ReactElement;
    href: string;
    label: string;
  }>;
  copyright?: string;
  legalLinks?: Array<{
    name: string;
    href: string;
  }>;
}
const defaultSections = [{
  title: 'Company',
  links: [{
    name: 'What We Do',
    href: '/what-we-do'
  }, {
    name: 'About Us',
    href: '/about'
  }, {
    name: 'Team',
    href: '/team'
  }, {
    name: 'Partners',
    href: '/partners'
  }]
}, {
  title: 'Services',
  links: [{
    name: 'Strategy Services',
    href: '/services'
  }, {
    name: 'MDM Implementation',
    href: '/services'
  }, {
    name: 'Data Governance',
    href: '/services'
  }, {
    name: 'Cloud Data Strategy',
    href: '/services'
  }]
}, {
  title: 'Resources',
  links: [{
    name: 'What is MDM?',
    href: '/what-is-mdm'
  }, {
    name: 'Contact',
    href: '/contact'
  }, {
    name: 'Book a Consult',
    href: 'https://vantagedatagroup.com/contact'
  }]
}];
const defaultSocialLinks = [{
  icon: <Youtube className="w-5 h-5" data-id="element-183" />,
  href: 'https://www.youtube.com/channel/UCRdkvCtyZdLVQSEjf4_VvrQ?view_as=subscriber',
  label: 'YouTube'
}, {
  icon: <Linkedin className="w-5 h-5" data-id="element-184" />,
  href: 'https://www.linkedin.com/in/brigitteworkman/',
  label: 'LinkedIn'
}];
const defaultLegalLinks = [{
  name: 'Terms and Conditions',
  href: '/terms'
}, {
  name: 'Privacy Policy',
  href: '/privacy'
}];
export const Footer = ({
  logo = {
    url: '/',
    src: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bWFakPm72iGj7zFK9vUdN4/vantaeg2.png',
    alt: 'ProviderMDM Logo',
    title: 'ProviderMDM'
  },
  sections = defaultSections,
  description = 'Enterprise data strategists who deliver premium insights at scale, trusted by healthcare leaders who value speed, precision, and successful outcomes.',
  socialLinks = defaultSocialLinks,
  copyright = '© 2025 Vantage Data Group. All rights reserved.',
  legalLinks = defaultLegalLinks
}: FooterProps) => {
  const handleLogoClick = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  return <section className="py-16 bg-black border-t border-white/10" data-id="element-185">
      <div className="container mx-auto px-6 lg:px-12" data-id="element-186">
        <div className="flex w-full flex-col justify-between gap-10 lg:flex-row lg:items-start lg:text-left" data-id="element-187">
          <div className="flex w-full flex-col justify-between gap-6 lg:items-start" data-id="element-188">
            <div className="flex items-center gap-3 lg:justify-start" data-id="element-189">
              <Link to={logo.url} onClick={handleLogoClick} data-id="element-190">
                <motion.img src={logo.src} alt={logo.alt} title={logo.title} className="h-10 w-auto" whileHover={{
                scale: 1.1
              }} transition={{
                duration: 0.2
              }} data-id="element-191" />
              </Link>
            </div>
            <p className="max-w-md text-sm text-white/70 leading-relaxed" data-id="element-192">
              {description}
            </p>
            <ul className="flex items-center space-x-6 text-white/70" data-id="element-193">
              {socialLinks.map((social, idx) => <li key={idx} className="hover:text-purple-400 transition-colors duration-300" data-id="element-194">
                  <a href={social.href} aria-label={social.label} target="_blank" rel="noopener noreferrer" data-id="element-195">
                    {social.icon}
                  </a>
                </li>)}
            </ul>
          </div>
          <div className="grid w-full gap-8 md:grid-cols-3 lg:gap-12" data-id="element-196">
            {sections.map((section, sectionIdx) => <div key={sectionIdx} data-id="element-197">
                <h3 className="mb-4 font-semibold text-white" data-id="element-198">
                  {section.title}
                </h3>
                <ul className="space-y-3 text-sm text-white/70" data-id="element-199">
                  {section.links.map((link, linkIdx) => <li key={linkIdx} className="hover:text-purple-400 transition-colors duration-300" data-id="element-200">
                      {link.href.startsWith('http') ? <a href={link.href} target="_blank" rel="noopener noreferrer" data-id="element-201">
                          {link.name}
                        </a> : <Link to={link.href} data-id="element-202">{link.name}</Link>}
                    </li>)}
                </ul>
              </div>)}
          </div>
        </div>
        <div className="mt-12 flex flex-col justify-between gap-4 border-t border-white/10 pt-8 text-xs text-white/60 md:flex-row md:items-center" data-id="element-203">
          <p className="order-2 lg:order-1" data-id="element-204">{copyright}</p>
          <ul className="order-1 flex flex-col gap-4 md:order-2 md:flex-row" data-id="element-205">
            {legalLinks.map((link, idx) => <li key={idx} className="hover:text-purple-400 transition-colors duration-300" data-id="element-206">
                <a href={link.href} data-id="element-207">{link.name}</a>
              </li>)}
          </ul>
        </div>
      </div>
    </section>;
};